// useMetaPixel — loads Meta's fbevents.js (the "Meta pixel") on the
// marketing site, and only once the visitor has actively consented.
//
// Deliberately the same shape as useGoogleAdsTag: same two gates, same
// double-injection guard, same empty-id kill switch. Read that file first —
// the reasoning there applies here unchanged, and the two are meant to stay
// symmetrical so a future edit to the consent policy can't fix one channel
// and miss the other.
//
//   1. isMarketingHost() — the pixel belongs on stemne.no. A tenant's
//      booking page is the operator's surface, not Stemne's ad funnel.
//      This matters MORE for Meta than for Google: operators include
//      terapeuter and massører, so a pixel on a tenant booking page would
//      ship end-customer appointment behaviour for health-adjacent
//      services to Meta. That is not a mistuned tag, it is an incident.
//   2. consent === 'granted' — 'unset' and 'denied' both mean don't load.
//
// One real asymmetry with Google, and it is the reason gate 2 carries more
// weight here: Meta has no Consent Mode. There is no default-deny signal
// that makes a loaded-but-unconsented pixel record nothing. Not loading the
// script IS the entire mechanism before a yes. So where useGoogleAdsTag has
// belt and braces, this has only the belt — do not weaken it. After a yes
// that is later withdrawn (the banner can be reopened), the script is already
// running, so the only lever left is Meta's own `fbq('consent', 'revoke')`.
//
// That revoke is sticky for the rest of the page load; see the watcher below.
//
// Automatic Advanced Matching is switched OFF before init. Left on (the
// Meta default) the pixel scrapes form fields on the page — emails, phone
// numbers — hashes them client-side and sends them along. On a page whose
// primary form IS the signup form, that would quietly turn a measurement
// tag into a PII pipe. `fbq('set', 'autoConfig', false, …)` must precede
// `init`; after init the setting no longer applies to that pixel.
//
// The signup conversion was documented as a URL-based custom conversion in
// Meta Events Manager against /get-started/subdomain, firing off the PageView
// below — so this file carried NO fbq('track', 'CompleteRegistration'), on the
// reasoning that a hand-fired event plus a URL rule would double-count.
//
// That custom conversion does not exist. Checked 2026-09-07: the ad account
// returns zero custom conversions, so there has never been a rule to
// double-count against, and Meta has never been able to attribute a signup.
// The Google side had the identical defect and identical reasoning, and its
// conversion action had literally never received a request in ~90 days (see
// useGoogleAdsTag). Two networks, one wrong assumption, written twice.
//
// reportSignupConversion below now sends the STANDARD event
// CompleteRegistration. Standard events are attributable with no Events
// Manager setup, which is why this side needs no equivalent of Google's
// conversion-label env var.
//
// Empty VITE_META_PIXEL_ID disables the whole thing (dev / CI path).

import { watch } from 'vue'
import { useCookieConsent } from '@/composables/useCookieConsent'
import { isMarketingHost } from '@/utils/platformHost'

const SCRIPT_ID = 'meta-pixel'
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js'
const PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined) ?? ''

interface FbqFn {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue: unknown[]
  push: unknown
  loaded: boolean
  version: string
}

interface FbqWindow extends Window {
  fbq?: FbqFn
  _fbq?: FbqFn
}

// The fbq shim, hand-written rather than pasted as Meta's one-liner.
// Semantics are identical: before fbevents.js arrives, every call is
// queued; after it arrives it installs `callMethod` and the queue is
// replayed. We push a real array rather than the `arguments` object for
// the same reason pushGtag does — the replay path is `.apply()`, which
// takes either, and an array is far easier to assert on in a test.
function ensureFbq(): FbqFn {
  const win = window as FbqWindow
  const existing = win.fbq
  if (existing) return existing

  const fbq = ((...args: unknown[]): void => {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, args)
    else fbq.queue.push(args)
  }) as FbqFn
  fbq.queue = []
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'

  win.fbq = fbq
  win._fbq = win._fbq ?? fbq
  return fbq
}

/** Queue one fbq command. Exported so tests (and any future caller) go
 *  through one door, mirroring pushGtag's single-writer rule. */
export function pushFbq(...args: unknown[]): void {
  ensureFbq()(...args)
}

// Module-scoped so neither a second useMetaPixel() call nor a consent ref
// that churns (granted → denied → granted) can inject a second <script>.
// The DOM check covers a pixel that survived from a prior module instance.
let injected = false

// Set once a withdrawal has sent `fbq('consent', 'revoke')`. Never cleared in
// this page load — see the watcher for why a re-grant does not undo it.
let revoked = false

function injectPixel(pixelId: string): void {
  if (injected) return
  injected = true
  if (!document.getElementById(SCRIPT_ID)) {
    const s = document.createElement('script')
    s.id = SCRIPT_ID
    s.async = true
    s.src = SCRIPT_SRC
    document.head.appendChild(s)
  }
  // Order is load-bearing: autoConfig off, then init, then PageView.
  pushFbq('set', 'autoConfig', false, pixelId)
  pushFbq('init', pixelId)
  pushFbq('track', 'PageView')
}

// One conversion per browser, same rule and same reasoning as the Google side.
const SIGNUP_FIRED_KEY = 'stemne.meta.signupConversionFired'

function alreadyFired(): boolean {
  try {
    return localStorage.getItem(SIGNUP_FIRED_KEY) === '1'
  } catch {
    return false
  }
}

function markFired(): void {
  try {
    localStorage.setItem(SIGNUP_FIRED_KEY, '1')
  } catch {
    // The event still went out, which is the point.
  }
}

/**
 * reportSignupConversion — tell Meta a signup completed.
 *
 * Sends the STANDARD event CompleteRegistration. Standard events are
 * attributable without any Events Manager configuration, which is the whole
 * reason this side does not need a second env var: the URL-based custom
 * conversion this replaces was never actually created (see the header).
 *
 * Safe to call repeatedly; only the first call in a browser sends anything.
 * Returns true when an event was sent, so tests assert on the return rather
 * than reaching into window.fbq.
 */
export function reportSignupConversion(): boolean {
  if (!PIXEL_ID || !isMarketingHost()) return false

  // Re-read at fire time. The pixel has no Consent Mode fallback, so a
  // decline after injection has to stop the event here or nowhere.
  const { consent } = useCookieConsent()
  if (consent.value !== 'granted') return false

  // Revoked earlier in this page load: fbevents would hold the event and
  // nothing goes out, so do not burn the once-per-browser flag on it. The
  // conversion fires after the email-verify page load, i.e. on a fresh load
  // that re-reads the stored decision, so in practice this is reported there.
  if (revoked) return false

  if (alreadyFired()) return false
  markFired()

  pushFbq('track', 'CompleteRegistration')
  return true
}

export function useMetaPixel(): void {
  if (!PIXEL_ID || !isMarketingHost()) return
  const { consent } = useCookieConsent()
  // No stop handle: this is called once from the app root and must outlive
  // every route change, so the watcher's lifetime is the app's lifetime.
  //
  // Withdrawal after a yes sends Meta's own consent switch, `fbq('consent',
  // 'revoke')`, and that revoke lasts until the next page load. A re-grant in
  // the same page deliberately sends NO `fbq('consent', 'grant')`: fbevents
  // holds every command issued while revoked and flushes them all on grant
  // (nuxt/scripts#930), and it auto-fires a PageView on every SPA history
  // change. Revoke → browse → grant would therefore ship the PageViews from
  // the withdrawn stretch to Meta after all. So Meta stays paused, and the
  // next page load re-reads the stored 'granted' and injects fresh. Google
  // (Consent Mode update) and OpenAI (`consent true`) resume immediately.
  //
  // Edge case: grant → fbevents.js still downloading → decline leaves the
  // queue as [set autoConfig, init, track PageView, consent revoke], and
  // fbevents replays it in order, so that one PageView (consented while
  // queued) still goes out — same as the OpenAI "still downloading" note.
  // The no-replay guarantee for conversions is reportSignupConversion
  // re-reading consent (and `revoked`) at fire time, not this revoke call.
  //
  // Never touch fbq when the pixel was never injected: a page that never
  // consented must not grow a `window.fbq` footprint just to be told no.
  // flush 'sync', like useOpenAIAdsPixel, so the revoke lands before any
  // other code runs on the tick the visitor declined.
  watch(
    consent,
    (value) => {
      if (value === 'granted') {
        if (!revoked) injectPixel(PIXEL_ID)
      } else if (injected && !revoked) {
        pushFbq('consent', 'revoke')
        revoked = true
      }
    },
    { immediate: true, flush: 'sync' },
  )
}
